import { useEffect, useState } from "react";
import { StatsCards } from "./StatsCards";
import { SalesChart } from "./SalesChart";
import { DonutChart } from "./DonutChart";
import { dashboardService, DashboardData } from "../services/dashboard";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/Select";
import { Button } from "./ui/Button";

export function DashboardContent() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<"currentMonth" | "lastMonth" | "custom">("currentMonth");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const loadDashboard = async () => {
    setLoading(true);
    try {
      let startDate = "";
      let endDate = "";

      const now = new Date();
      if (period === "currentMonth") {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        startDate = firstDay.toISOString().split("T")[0];
        endDate = lastDay.toISOString().split("T")[0];
      } else if (period === "lastMonth") {
        const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        startDate = firstDay.toISOString().split("T")[0];
        endDate = lastDay.toISOString().split("T")[0];
      } else {
        startDate = customStartDate;
        endDate = customEndDate;
      }

      const response = await dashboardService.getDashboardData({
        start_date: startDate,
        end_date: endDate,
        period_type: period,
      });

      setData(response.data);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      toast.error("Erro ao carregar dados do dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (period !== "custom") {
      loadDashboard();
    }
  }, [period]);

  const handleCustomApply = () => {
    if (customStartDate && customEndDate) {
      loadDashboard();
    } else {
      toast.error("Selecione as datas de início e fim.");
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Visão geral das vendas e estatísticas</p>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
          <Select value={period} onValueChange={(value: any) => setPeriod(value)}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="currentMonth">Este mês</SelectItem>
              <SelectItem value="lastMonth">Mês passado</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {period === "custom" && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data inicial</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="border rounded px-3 py-2 w-40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data final</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="border rounded px-3 py-2 w-40"
              />
            </div>
            <Button
              type="button"
              onClick={handleCustomApply}
              className="border-gray-300 bg-[#D4C070] text-[#FFF] hover:bg-[#D4C070]"
            >
              Aplicar
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          <StatsCards data={data?.cards ?? null} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <div className="lg:col-span-2">
              {/* Agora o SalesChart recebe data.chart no novo formato */}
              <SalesChart data={data?.chart ?? null} />
            </div>
            <div className="lg:col-span-1">
              <DonutChart data={data?.donut ?? null} />
            </div>
          </div>
        </>
      )}
    </main>
  );
}